import React, { useEffect, useRef, useState } from "react";
import { loadGoogleMaps } from "./PlacesInput";

/**
 * Lightweight route preview for the New Reservation modal. Draws
 *   pickup → stop 1 → stop 2 → … → drop-off
 * using Google Maps DirectionsService. Updates whenever any address
 * changes or the dispatcher reorders the stops. Falls back silently if
 * Maps fails to load or any address can't be geocoded.
 */
export default function RoutePreviewMap({ pickup, stops = [], dropoff }) {
  const mapElRef = useRef(null);
  const mapRef = useRef(null);
  const rendererRef = useRef(null);
  const [meta, setMeta] = useState(null); // { distance, duration }
  const [err, setErr] = useState(null);

  // Build the request payload only when we have both endpoints filled in.
  const cleanStops = stops.map(s => (s || "").trim()).filter(Boolean);
  const ready = (pickup || "").trim().length > 3 && (dropoff || "").trim().length > 3;

  useEffect(() => {
    if (!ready) { setMeta(null); setErr(null); return; }
    let cancelled = false;
    loadGoogleMaps().then((google) => {
      if (cancelled) return;
      if (!mapRef.current) {
        mapRef.current = new google.maps.Map(mapElRef.current, {
          center: { lat: 43.0389, lng: -87.9065 }, // Milwaukee
          zoom: 10,
          disableDefaultUI: true,
          gestureHandling: "cooperative",
          styles: [
            // Dark theme to match the modal aesthetic
            { elementType: "geometry", stylers: [{ color: "#1d1d1d" }] },
            { elementType: "labels.text.fill", stylers: [{ color: "#8a8a8a" }] },
            { elementType: "labels.text.stroke", stylers: [{ color: "#000000" }] },
            { featureType: "road", elementType: "geometry", stylers: [{ color: "#2a2a2a" }] },
            { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#3a3a3a" }] },
            { featureType: "water", elementType: "geometry", stylers: [{ color: "#0d1419" }] },
            { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
            { featureType: "transit", stylers: [{ visibility: "off" }] },
          ],
        });
        rendererRef.current = new google.maps.DirectionsRenderer({
          map: mapRef.current,
          suppressMarkers: false,
          polylineOptions: { strokeColor: "#D4AF37", strokeWeight: 4, strokeOpacity: 0.9 },
        });
      }
      const svc = new google.maps.DirectionsService();
      svc.route(
        {
          origin: pickup,
          destination: dropoff,
          waypoints: cleanStops.map(s => ({ location: s, stopover: true })),
          optimizeWaypoints: false, // honour the dispatcher's manual ordering
          travelMode: google.maps.TravelMode.DRIVING,
        },
        (res, status) => {
          if (cancelled) return;
          if (status === "OK" && res) {
            rendererRef.current.setDirections(res);
            const legs = res.routes[0].legs;
            const meters = legs.reduce((a, l) => a + l.distance.value, 0);
            const seconds = legs.reduce((a, l) => a + l.duration.value, 0);
            setMeta({
              distance: (meters / 1609.34).toFixed(1) + " mi",
              duration: Math.round(seconds / 60) + " min",
              stops: cleanStops.length,
            });
            setErr(null);
          } else if (status === "REQUEST_DENIED") {
            // Directions API not enabled on the project — fall back to a
            // marker-only preview using the Geocoder so the dispatcher still
            // sees the geographic spread of pickup → stops → drop-off.
            geocodeFallback(google, [pickup, ...cleanStops, dropoff], mapRef.current);
            setErr("Route polyline unavailable · showing markers (enable Directions API)");
            setMeta(null);
          } else {
            setErr(`Could not route: ${status}`);
            setMeta(null);
          }
        }
      );
    }).catch((e) => setErr(e.message || "Maps unavailable"));
    return () => { cancelled = true; };
  }, [pickup, dropoff, JSON.stringify(cleanStops)]);

  // Marker-only fallback when Directions API isn't enabled. Geocodes each
  // address sequentially, drops a numbered pin, and fits the map bounds.
  const fallbackMarkersRef = useRef([]);
  const fallbackPolyRef = useRef(null);
  function geocodeFallback(google, addresses, map) {
    // Wipe previous markers / polyline from a prior render.
    (fallbackMarkersRef.current || []).forEach(m => m.setMap(null));
    fallbackMarkersRef.current = [];
    if (fallbackPolyRef.current) { fallbackPolyRef.current.setMap(null); fallbackPolyRef.current = null; }
    if (rendererRef.current) rendererRef.current.set("directions", null);

    const geocoder = new google.maps.Geocoder();
    const bounds = new google.maps.LatLngBounds();
    const path = [];
    let pending = addresses.length;
    addresses.forEach((addr, i) => {
      geocoder.geocode({ address: addr }, (results, status) => {
        pending -= 1;
        if (status === "OK" && results[0]) {
          const loc = results[0].geometry.location;
          path[i] = loc;
          bounds.extend(loc);
          const label = i === 0 ? "A"
            : i === addresses.length - 1 ? "B"
            : String(i);
          const marker = new google.maps.Marker({
            position: loc,
            map,
            label: { text: label, color: "#0A0A0A", fontWeight: "bold" },
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 12,
              fillColor: "#D4AF37",
              fillOpacity: 1,
              strokeColor: "#0A0A0A",
              strokeWeight: 2,
            },
          });
          fallbackMarkersRef.current.push(marker);
        }
        if (pending === 0) {
          const cleanPath = path.filter(Boolean);
          if (cleanPath.length >= 2) {
            fallbackPolyRef.current = new google.maps.Polyline({
              path: cleanPath, map,
              strokeColor: "#D4AF37", strokeWeight: 3, strokeOpacity: 0.8,
              geodesic: true,
            });
          }
          if (!bounds.isEmpty()) map.fitBounds(bounds, 40);
        }
      });
    });
  }

  if (!ready) return null;

  return (
    <div data-testid="route-preview-map" className="mt-3 border border-white/10 overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 bg-black/40 text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
        <span>Route preview · {cleanStops.length} stop{cleanStops.length === 1 ? "" : "s"}</span>
        {meta && (
          <span className="text-[#D4AF37]">{meta.distance} · {meta.duration}</span>
        )}
        {err && !meta && <span className="text-red-400 normal-case tracking-normal">{err}</span>}
      </div>
      <div ref={mapElRef} style={{ width: "100%", height: 220 }} className="bg-[#0A0A0A]"/>
    </div>
  );
}
